import type { ComponentType } from 'react';
import { ROLES, type UserRole } from '@/constants/roles';

export type PageDef = {
  path: string;
  titleKey: string;
  isPublic?: boolean;
  roles?: readonly UserRole[];
  load: () => Promise<{ default: ComponentType }>;
};

const TENANT_ROLES = [
  ROLES.RegularEmployee,
  ROLES.TeamLead,
  ROLES.TenantAdmin,
] as const;

export const PAGES = {
  // ── Pre-auth (tenant host) ──────────────────────────────────────────────
  signin: {
    path: '/signin',
    titleKey: 'signin.title',
    isPublic: true,
    load: () => import('@/pages/SignIn/SignInPage'),
  },
  passwordResetRequest: {
    path: '/password-reset',
    titleKey: 'passwordResetRequest.title',
    isPublic: true,
    load: () => import('@/pages/PasswordResetRequest/PasswordResetRequestPage'),
  },
  passwordResetConfirm: {
    path: '/password-reset/confirm',
    titleKey: 'passwordResetConfirm.title',
    isPublic: true,
    load: () => import('@/pages/PasswordResetConfirm/PasswordResetConfirmPage'),
  },
  authCallback: {
    path: '/auth/callback',
    titleKey: 'authCallback.title',
    isPublic: true,
    load: () => import('@/pages/AuthCallback/AuthCallbackPage'),
  },
  // ── Authenticated (tenant host, any signed-in tenant user) ──────────────
  timer: {
    path: '/timer',
    titleKey: 'timer.title',
    roles: TENANT_ROLES,
    load: () => import('@/pages/Timer/TimerPage'),
  },
  weekly: {
    path: '/weekly',
    titleKey: 'weekly.title',
    roles: TENANT_ROLES,
    load: () => import('@/pages/Weekly/WeeklyPage'),
  },
  calendar: {
    path: '/calendar',
    titleKey: 'calendar.title',
    roles: TENANT_ROLES,
    load: () => import('@/pages/Calendar/CalendarPage'),
  },
  reports: {
    path: '/reports',
    titleKey: 'reports.title',
    roles: TENANT_ROLES,
    load: () => import('@/pages/Reports/ReportsPage'),
  },
  profile: {
    path: '/profile',
    titleKey: 'profile.title',
    roles: TENANT_ROLES,
    load: () => import('@/pages/Profile/ProfilePage'),
  },
  // ── Authenticated (tenant host, TenantAdmin — and TeamLead for project detail) ─
  adminSettings: {
    path: '/admin/settings',
    titleKey: 'adminSettings.title',
    roles: [ROLES.TenantAdmin] as const,
    load: () => import('@/pages/AdminSettings/AdminSettingsPage'),
  },
  adminUsers: {
    path: '/admin/users',
    titleKey: 'adminUsers.title',
    roles: [ROLES.TenantAdmin] as const,
    load: () => import('@/pages/AdminUsers/AdminUsersPage'),
  },
  adminUserDetail: {
    path: '/admin/users/:userId',
    titleKey: 'adminUserDetail.title',
    roles: [ROLES.TenantAdmin] as const,
    load: () => import('@/pages/AdminUserDetail/AdminUserDetailPage'),
  },
  adminClients: {
    path: '/admin/clients',
    titleKey: 'adminClients.title',
    roles: [ROLES.TenantAdmin] as const,
    load: () => import('@/pages/AdminClients/AdminClientsPage'),
  },
  adminClientDetail: {
    path: '/admin/clients/:clientId',
    titleKey: 'adminClientDetail.title',
    roles: [ROLES.TenantAdmin] as const,
    load: () => import('@/pages/AdminClientDetail/AdminClientDetailPage'),
  },
  adminProjectDetail: {
    path: '/admin/projects/:projectId',
    titleKey: 'adminProjectDetail.title',
    roles: [ROLES.TenantAdmin, ROLES.TeamLead] as const,
    load: () => import('@/pages/AdminProjectDetail/AdminProjectDetailPage'),
  },
  adminTeams: {
    path: '/admin/teams',
    titleKey: 'adminTeams.title',
    roles: [ROLES.TenantAdmin] as const,
    load: () => import('@/pages/AdminTeams/AdminTeamsPage'),
  },
  adminTeamDetail: {
    path: '/admin/teams/:teamId',
    titleKey: 'adminTeamDetail.title',
    roles: [ROLES.TenantAdmin] as const,
    load: () => import('@/pages/AdminTeamDetail/AdminTeamDetailPage'),
  },
  adminAuditLog: {
    path: '/admin/audit-log',
    titleKey: 'adminAuditLog.title',
    roles: [ROLES.TenantAdmin] as const,
    load: () => import('@/pages/AdminAuditLog/AdminAuditLogPage'),
  },
  // ── Pre-auth (platform host) ────────────────────────────────────────────
  platformSignin: {
    path: '/platform/signin',
    titleKey: 'platformSignin.title',
    isPublic: true,
    load: () => import('@/pages/PlatformSignIn/PlatformSignInPage'),
  },
  // ── Authenticated (platform host, SuperAdmin) ───────────────────────────
  platformTenants: {
    path: '/platform/tenants',
    titleKey: 'platformTenants.title',
    roles: [ROLES.SuperAdmin] as const,
    load: () => import('@/pages/PlatformTenants/PlatformTenantsPage'),
  },
  platformTenantDetail: {
    path: '/platform/tenants/:tenantId',
    titleKey: 'platformTenantDetail.title',
    roles: [ROLES.SuperAdmin] as const,
    load: () => import('@/pages/PlatformTenantDetail/PlatformTenantDetailPage'),
  },
  platformTenantAuditLog: {
    path: '/platform/tenants/:tenantId/audit-log',
    titleKey: 'platformTenantAuditLog.title',
    roles: [ROLES.SuperAdmin] as const,
    load: () => import('@/pages/PlatformTenantAuditLog/PlatformTenantAuditLogPage'),
  },
  platformAuditLog: {
    path: '/platform/audit-log',
    titleKey: 'platformAuditLog.title',
    roles: [ROLES.SuperAdmin] as const,
    load: () => import('@/pages/PlatformAuditLog/PlatformAuditLogPage'),
  },
  platformLanguages: {
    path: '/platform/languages',
    titleKey: 'platformLanguages.title',
    roles: [ROLES.SuperAdmin] as const,
    load: () => import('@/pages/PlatformLanguages/PlatformLanguagesPage'),
  },
} as const satisfies Record<string, PageDef>;

export type PageKey = keyof typeof PAGES;
